import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { CalendarDays } from "lucide-react";

export const Route = createFileRoute("/teacher/timetable")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Timetable · MAKAI Teachers" }] }),
  component: Page,
});

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat"] as const;
const DAY_LABEL: Record<string, string> = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

type Slot = {
  id: string; day: string; start_time: string; end_time: string; room: string | null;
  subject: { name: string } | null; stream: { name: string; class: { name: string } | null } | null;
};

function Page() {
  const ctx = useTeacherContext();
  const [slots, setSlots] = useState<Slot[]>([]);
  const today = (["sun","mon","tue","wed","thu","fri","sat"] as const)[new Date().getDay()];

  useEffect(() => {
    if (!ctx.staffId) return;
    supabase.from("timetable_slots")
      .select("id, day, start_time, end_time, room, subject:subjects_catalog(name), stream:streams(name, class:classes(name))")
      .eq("staff_id", ctx.staffId)
      .order("start_time")
      .then(({ data }) => setSlots((data ?? []) as any));
  }, [ctx.staffId]);

  // Build unique time rows
  const times = Array.from(new Set(slots.map((s) => s.start_time))).sort();

  return (
    <AppShell role="teacher" title="My Timetable" subtitle="Weekly view of all your lessons">
      {slots.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-border bg-card p-12 text-center text-sm text-muted-foreground">
          <CalendarDays className="mb-2 h-8 w-8" /> No timetable slots assigned yet. Ask your admin to set them.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase text-muted-foreground">Time</th>
                {DAYS.map((d) => (
                  <th key={d} className={`px-3 py-2 text-left text-xs font-bold uppercase ${d === today ? "text-primary" : "text-muted-foreground"}`}>{DAY_LABEL[d]}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {times.map((t) => (
                <tr key={t}>
                  <td className="px-3 py-2 text-xs font-semibold text-muted-foreground">{t.slice(0, 5)}</td>
                  {DAYS.map((d) => {
                    const s = slots.find((x) => x.day === d && x.start_time === t);
                    return (
                      <td key={d} className="px-2 py-2 align-top">
                        {s ? (
                          <div className={`rounded-xl border p-2 ${d === today ? "border-primary/40 bg-primary-soft" : "border-border bg-background/60"}`}>
                            <div className="text-sm font-semibold">{s.subject?.name ?? "Lesson"}</div>
                            <div className="text-[11px] text-muted-foreground">{s.stream?.class?.name} · {s.stream?.name}</div>
                            {s.room && <div className="text-[11px] text-muted-foreground">Room {s.room}</div>}
                            <div className="text-[11px] text-muted-foreground">{s.start_time.slice(0,5)}–{s.end_time.slice(0,5)}</div>
                          </div>
                        ) : <span className="text-xs text-muted-foreground/40">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
